import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { selectSupplierInfo } from "../../../../store/slices/supplierInfoSlice";
import { formatValueToLocale, roundToTwo } from "../../../../utils/method";

const CalculationTableV2 = () => {
    const { t } = useTranslation();
    const supplierInfo = useSelector(selectSupplierInfo);
    const { amounts = {} } = supplierInfo || {};

    const calculatedRows = amounts.calculatedRows ?? [];

    // ── Render helpers ─────────────────────────────────────────────────────────
    // The sign shown must reflect the actual signed value, not the static
    // isDeduction flag: corrections can be positive credits (correctionsCredit)
    // that the backend adds to the total, so a forced minus would contradict the
    // math. Deductions are stored negative and still render with a minus.
    const renderAmount = (value) => {
        const rounded = roundToTwo(value ?? 0);
        const formatted = formatValueToLocale(Math.abs(rounded), true);
        if (rounded === 0) return <span>{formatted}</span>;
        const isNegative = rounded < 0;
        return (
            <span className={isNegative ? "calc-table-deduction" : ""}>
                {isNegative ? <span className="calc-table-minus">- </span> : null}
                {formatted}
            </span>
        );
    };

    const rowLabel = (row) => {
        if (row.rawLabel) return row.rawLabel;
        return t(`supplier-info.calc-table.${row.labelKey}`, row.labelParams ?? {});
    };

    const rowSubLabel = (row) => {
        if (!row.skontoDate) return null;
        return t("supplier-info.calc-table.skonto-date-hint", { date: row.skontoDate });
    };

    return (
        <div className="calc-table-v2-wrapper">
            <div className="table-responsive">
                <table className="table table-sm table-bordered calc-table-v2">
                    <thead>
                        <tr className="calc-table-header">
                            <th className="calc-table-col-nr">{t("supplier-info.calc-table.nr")}</th>
                            <th className="calc-table-col-label">{t("supplier-info.calc-table.bezeichnung")}</th>
                            <th className="calc-table-col-amount text-end">{t("supplier-info.calc-table.netto")}</th>
                            <th className="calc-table-col-amount text-end">{t("supplier-info.calc-table.mwst")}</th>
                            <th className="calc-table-col-amount text-end">{t("supplier-info.calc-table.brutto")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {calculatedRows.map((row, idx) => (
                            <tr
                                key={row.key}
                                className={[
                                    "calc-table-row",
                                    row.isBold ? "calc-table-row--bold" : "",
                                ].filter(Boolean).join(" ")}
                            >
                                <td className="calc-table-col-nr">{idx + 1}</td>
                                <td className="calc-table-col-label">
                                    {rowLabel(row)}
                                    {rowSubLabel(row) && (
                                        <div className="calc-table-sub-label">{rowSubLabel(row)}</div>
                                    )}
                                </td>
                                <td className="calc-table-col-amount text-end">
                                    {renderAmount(row.net)}
                                </td>
                                <td className={`calc-table-col-amount text-end${row.netOnly ? " calc-table-cell--grey" : ""}`}>
                                    {!row.netOnly && renderAmount(row.vat)}
                                </td>
                                <td className={`calc-table-col-amount text-end${row.netOnly ? " calc-table-cell--grey" : ""}`}>
                                    {!row.netOnly && renderAmount(row.gross)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default CalculationTableV2;
